import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate } from '@angular/common';
import { Orders } from 'src/app/models/Orders.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import {
  SortDirection,
  nextSortState,
  sortItems,
} from 'src/app/utils/table-sort';

/**
 * StoricoParzialiComponent — storico ordini parziali (closed +
 * `or_partial === 'S'`). Lista filtrabile con click → modale
 * dettagli (sub-modale `<app-storico-ordini-dettagli>`, riusata
 * dallo storico ordini completo).
 *
 * Phase 1.5 / v2.8.0 — TS cleaned up (Chunk G.1):
 *   • dropped kendo `RowClassArgs` import + `roWow` / `combinedRowClass`
 *     callback (servivano alla kendo-grid, non più necessaria con la
 *     table Diakonix nativa — il colore di stato è gestito via
 *     `data-status` attribute + CSS),
 *   • dropped la legenda "accordion" (`isLegendaOpen`,
 *     `legendaItems`, `toggleLegenda`, `openLegenda`, `closeLegenda`)
 *     — sostituita da una chip viola persistente accanto al sottotitolo,
 *     più compatta e sempre visibile,
 *   • dropped duplicato `getOrders()` (era una versione non-filtrata
 *     mai chiamata nel template attivo),
 *   • dropped state: `orderz`, `dates`, `customer`, `date`, `dateBy`,
 *     `customerIndex`, `showAll2`, `clientOrder`, `searchString`,
 *     `searchDate`, `toDate`, `opened`, `pageSize` (kendo-grid only),
 *   • dropped console.log/debug (15+),
 *   • dropped `onMessengerNameSelect` + `onDateSelect`
 *     (i 2 dropdown del legacy sono rimpiazzati da 1 solo dropdown
 *     cliente nel pattern Diakonix — la dropdown data era ridondante
 *     col date-range già presente),
 *   • dropped `startSearch` (1-liner che impostava un flag mai letto),
 *   • dropped `removeData` (alias di clearSearch + getOrders, fuso),
 *   • removed `Router` injection (zero usage),
 *   • switch to inject() + takeUntilDestroyed.
 *
 * Behaviour preserved 1:1: load getClosedOrders → filter
 * or_partial='S', search by term, date range filter applicato lato
 * template via pipe `byBordero`, click row → openStorico modal.
 */
@Component({
  selector: 'app-storico-parziali',
  templateUrl: './storico-parziali.component.html',
  styleUrls: ['./storico-parziali.component.scss'],
  standalone: false,
})


export class StoricoParzialiComponent implements OnInit {

  private readonly orderSrv = inject(OrdersService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeSrv = inject(OpenModalService);

  // ── Data ────────────────────────────────────────────────────────
  public ordersStock: Orders.IOrderClosed[] = [];
  public customers: string[] = [];

  // ── UI state ────────────────────────────────────────────────────
  public loading = false;
  public isSearched = false;
  public filterTerm: string | undefined;
  public clickedOrder = '';
  public selectedCustomer: string | null = null;
  public readonly showAll = 'Mostra tutti';

  public sortColumn: string | null = null;
  public sortDirection: SortDirection = 'asc';

  // ── Date range filter (defaults: 2021-01-01 → today) ───────────
  startDate: Date = new Date(2021, 0, 1);
  endDate: Date = new Date();

  filters = {
    dateOrginizedBy: {
      text: 'Data ordine',
      value: 'or_order_number',
    },
    startingDate: this.startDate as Date | null,
    endDate: this.endDate as Date,
    minDate: new Date(),
    maxDate: new Date(),
  };

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loading = true;
    this.getClosedPartialOrders();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loader.
  // ──────────────────────────────────────────────────────────────────

  private getClosedPartialOrders(): void {
    this.orderSrv
      .getClosedOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ordersData) => {
          if (!ordersData) {
            this.ordersStock = [];
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.ordersStock = ordersData.filter(
            (order) => order.or_partial === 'S',
          );
          this.rebuildCustomers(ordersData);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private rebuildCustomers(ordersData: Orders.IOrderClosed[]): void {
    const set = new Set(ordersData.map((o) => o.or_customer ?? ''));
    this.customers = [this.showAll, ...Array.from(set)];
  }

  // ──────────────────────────────────────────────────────────────────
  // Search + filters.
  // ──────────────────────────────────────────────────────────────────

  searchOrders(search?: string): void {
    if (search !== undefined) this.filterTerm = search;
    if (!this.filterTerm || this.filterTerm.trim() === '') {
      this.isSearched = false;
      this.getClosedPartialOrders();
      return;
    }
    this.isSearched = true;
    this.orderSrv
      .searchClosedOrders(this.filterTerm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: IWsResult) => {
          if (result?.error_message?.msg_code !== 0) {
            this.ordersStock = [];
            this.cdr.detectChanges();
            return;
          }
          this.ordersStock = (result.data ?? []).filter(
            (order: Orders.IOrderClosed) => order.or_partial === 'S',
          );
          this.rebuildCustomers(result.data ?? []);
          this.cdr.detectChanges();
        },
      });
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.isSearched = false;
    this.filters.startingDate = this.startDate;
    this.selectedCustomer = null;
    this.getClosedPartialOrders();
  }

  onCustomerChange(name: string | null): void {
    this.selectedCustomer =
      !name || name === this.showAll ? null : name;
  }

  /** Filtro client-side combinato: customer + date range applicati
   *  alla lista `ordersStock` già caricata e filtrata partial='S'.
   *
   *  🚨 FIX v2.12.6: il sort viene applicato QUI nel getter (non più
   *  in applyView() che mutava `ordersStock` mutilando i dati raw).
   *  Pattern: ordersStock = source-of-truth immutable, filteredOrders
   *  getter = view derivata (filter + sort) ricomputata ad ogni CD.
   *  Drop completo di applyView() (era legacy + buggata). */
  get filteredOrders(): Orders.IOrderClosed[] {
    const customer = this.selectedCustomer;
    const start = this.filters.startingDate;
    const end = this.filters.endDate;
    let items = this.ordersStock.filter((o) => {
      if (customer && o.or_customer !== customer) return false;
      if (start && o.or_order_date && new Date(o.or_order_date) < start) {
        return false;
      }
      if (end && o.or_order_date && new Date(o.or_order_date) > end) {
        return false;
      }
      return true;
    });
    if (this.sortColumn) {
      items = sortItems(items, this.sortColumn, this.sortDirection);
    }
    return items;
  }

  onStartDateChange(value: Date | string | null): void {
    if (!value) {
      this.filters.startingDate = null;
      return;
    }
    this.filters.startingDate = value instanceof Date ? value : new Date(value);
  }

  onEndDateChange(value: Date | string | null): void {
    if (!value) return;
    this.filters.endDate = value instanceof Date ? value : new Date(value);
  }

  // ──────────────────────────────────────────────────────────────────
  // UI helpers.
  // ──────────────────────────────────────────────────────────────────

  /** Maps the legacy `tab_order_status_cod` to a visual state used
   *  by the row CSS (left-border colour). */
  statusState(order: Orders.IOrderClosed): string {
    switch (order.tab_order_status_cod) {
      case 1:
        return 'ok';
      case 2:
      case 3:
      case 4:
      case 5:
      case 10:
        return 'progress';
      case 100:
        return 'closed';
      case 101:
        return 'danger';
      default:
        return 'default';
    }
  }

  formatDateIt(value: string | Date | null | undefined): string {
    if (!value) return '—';
    try {
      return formatDate(value, 'dd/MM/yyyy', 'it');
    } catch {
      return '—';
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Detail navigation.
  // ──────────────────────────────────────────────────────────────────

  goToDetails(order_id: string | number): void {
    this.clickedOrder = String(order_id);
    this.closeSrv.onStorico();
  }

  /** 🚨 FIX v2.12.6: applyView() era buggato — mutava `ordersStock`
   *  invece di leggere dal source-of-truth + applicare filter/sort
   *  ad ogni read. Drop completo, il template ora legge `filteredOrders`
   *  getter che fa tutto il lavoro (filter + sort) on-demand.
   *  Side-effect del drop: il sort cliccando i thead funziona davvero
   *  ora (era invisibile prima perché applyView mutava la sorgente
   *  ma il template leggeva il getter su un array già mutato). */
  sortBy(column: string): void {
    const next = nextSortState(this.sortColumn, this.sortDirection, column);
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.cdr.detectChanges();
  }
  
}
